// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-02-06 21:18:12 +0100 (Thu, 06 Feb 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 150023 $
//  Purpose   : customized tables (using react-table)
// =============================================================================
// ---Third-party imports
import React from 'react'
import {ReactTable} from 'uix/chunks'
//~ import 'react-table/react-table.css'
import {Label, Icon, Checkbox, Button} from 'semantic-ui-react'
import clsx from 'clsx'
import {isElement, isFragment} from 'react-is'

// ---Framework imports
import {Sel} from 'uix/registries'
import {
  get,
  filterItems,
  mapItems,
  dict,
  size,
  countBy,
  flatten,
  isString,
  isArray,
  isObject,
  isSet,
  isFunction,
  callIfFunc,
  sorted,
  isNil,
  uniqBy,
  sum,
} from 'ldash'
import log from 'uix/logging'
import {
  useRef,
  useState,
  usePartContext,
  useMemo,
  useSelector,
  usePersistent,
  useEffectSSR as useEffect,
  useOverflowDetection,
  useCallback,
  usePersistentShallow,
  useRouter,
} from 'uix/hooks'

// ---Local imports
import styles from './styles.scss'
import {AutoPortal} from '../portals'
import {XMenu} from '../menu'
import {NUMBER_TYPE, CATEGORIAL_ALL, getNumberFilterFunc} from './cols'
import {VIcon} from 'graphics'

// ---Exports

export const copyButton = (data, label, title) => (
  <Button
    basic
    color="grey"
    fluid
    icon
    disabled={!data}
    title={title}
    onClick={() => navigator.clipboard.writeText(data)}
    labelPosition="left"
  >
    <Icon name="copy" />
    {label}
  </Button>
)

const buildPersistKey = (name, suffix) => (suffix ? `${name}_${suffix}` : name)

export {filterNumbers, filterMatchSorter} from './cols'
const SEP1 = ':'
// needs to be escaped in values
const SEP2 = ','
// ugly escaping (with \SEP2)
let join_sep2 = l => l.map(e => e.replaceAll(SEP2, `\\${SEP2}`)).join(SEP2)
let split_sep2 = l =>
  l
    .replaceAll(`\\${SEP2}`, '\\~')
    .split(SEP2)
    .map(e => e.replaceAll('\\~', SEP2))

// copy of rt6 in defaultprops.js
const defaultSortMethod = (a, b, desc, row_a, row_b) => {
  // for pivotby col, sort by number of children
  if (row_a._subRows && row_b._subRows) return row_a._subRows.length - row_b._subRows.length
  // XXX: we could also call the tr6 defaults from here

  // force null and undefined to the bottom
  a = a === null || a === undefined ? '' : a
  b = b === null || b === undefined ? '' : b
  // force any string values to lowercase
  a = typeof a === 'string' ? a.toLowerCase() : a
  b = typeof b === 'string' ? b.toLowerCase() : b
  // Return either 1 or -1 to indicate a sort priority
  if (a > b) {
    return 1
  }
  if (a < b) {
    return -1
  }
  // returning 0, undefined or any falsey value will use subsequent sorts or
  // the index as a tiebreaker
  return 0
}

export const buildReactTableOpts = (colids, storedOpts) => {
  /* build a query param representation of `filtered` and `sorted` values
   */

  let {filtered = [], sorted = []} = storedOpts
  let isActive = ({id}) => colids.includes(id)

  let filteropts = join_sep2(
    filtered
      .filter(isActive)
      .map(({id, value}) => {
        if (isNil(value)) {
          return null
        } else if (isObject(value)) {
          if (value.type == NUMBER_TYPE && value.text) {
            return `${id}${SEP1}num${SEP1}${value.text}`
          }
          return null
        } else {
          if (value === CATEGORIAL_ALL) {
            // XXX: we should test if it's really a categorial/option typed filter
            return null
          }
          return `${id}${SEP1}${value}`
        }
      })
      .filter(e => e)
  )

  let sortopts = join_sep2(sorted.filter(isActive).map(({id, desc}) => `${desc ? '' : '^'}${id}`))
  return {filtered: filteropts, sorted: sortopts}
}
const useQueryParamOverrideReactTable = (opts, setOpts) => {
  /* decode query param representation of `filtered` and `sorted` params
   */
  let {request} = useRouter()
  useEffect(() => {
    // derive options from query params
    let params = dict(
      ['filtered', 'sorted'].filter(n => !isNil(request.query[n])).map(n => [n, request.query[n]])
    )
    if (params.filtered) {
      let filtered = split_sep2(params.filtered).map(name => {
        let [id, ...rest] = name.split(SEP1)
        let value = rest.join(SEP1)
        if (rest[0] == 'num') {
          value = rest.slice(1).join(SEP1)
          return {id, value: {text: value, type: NUMBER_TYPE, func: getNumberFilterFunc(value)}}
        } else {
          return {id, value}
        }
      })
      params.filtered = filtered // uniqBy([...filtered, ...opts.filtered], e => e.id)
    }
    if (params.sorted) {
      let sorted = split_sep2(params.sorted).map(name => {
        let desc = true
        if (name.startsWith('^')) {
          name = name.slice(1)
          desc = false
        }
        return {id: name, desc}
      })
      params.sorted = sorted
    }
    if (size(params) > 0) {
      setOpts(params)
    }
  }, [request])
}

// XXX: storing "resized" is pretty intrusive
// resized would need some sort of manual reset
// resized should depend on stable number of active/visible columns
// resized: []
const TABLE_STATE = {sorted: [], filtered: [], expanded: {}}
export const usePersistentTableStateRaw = persistKey =>
  usePersistentShallow(buildPersistKey('TableOptions', persistKey), TABLE_STATE, 2)

const usePersistentTableState = ({persistKey, onFilteredChange, data, pivotBy}) => {
  /* persist table state in part context
  - sorting
  - resized
  - filtered
   */
  let [opts, setOpts] = usePersistentTableStateRaw(persistKey)
  opts = useMemo(() => {
    if (opts.filtered) {
      // restore number filter func
      opts = {
        ...opts,
        filtered: opts.filtered.map(e => {
          if (e.value?.type == 'number' && !e.value.func) {
            return {...e, value: {...e.value, func: getNumberFilterFunc(e.value.text)}}
          }
          return e
        }),
      }
    }
    return opts
  }, [opts])

  useQueryParamOverrideReactTable(opts, setOpts)

  // XXX: do we really need to protect us against changes here?
  // pick values from nested {text, func} (NumberFilter) or directly from value
  return useMemo(() => {
    let str_val = v => {
      if (isObject(v)) {
        // NumberFilter
        return v.text
      } else if (isSet(v)) {
        // categorial multi value set
        return [...v].join(',')
      } else {
        return v
      }
    }
    let calc_vals = f =>
      sorted(f, e => e.id)
        .map(e => e.id + '=' + str_val(e.value))
        .join('_')

    return {
      ...opts,
      onSortedChange: sorted => setOpts({sorted}),
      onFilteredChange: filtered => {
        let is_different = calc_vals(filtered) !== calc_vals(opts.filtered)
        //~ log.x('%o', {is_different, filtered, other: opts.filtered, x: calc_vals(filtered)})
        if (is_different) {
          //~ log.x('ONCHANGE: %o %o', calc_vals(filtered), calc_vals(opts.filtered))
          callIfFunc(onFilteredChange, filtered)
          setOpts({filtered})
        }
      },

      // disabled: depends on active cols
      //~ onResizedChange: useCallback(resized => setOpts({resized}), []),
    }
  }, [opts, setOpts, onFilteredChange])
}

const useFocusManagement = (focused, selectionKey, onFocusChange, getTrProps) => {
  let [_focused, setFocused] = useState(null)
  let changeFocus = focus_id => {
    setFocused(focus_id)
    onFocusChange(focus_id)
  }
  if (focused !== undefined) {
    _focused = focused
  }
  if (onFocusChange) {
    let _getTrProps = (state, rowInfo, column) => {
      if (!rowInfo?.original) return tr_props
      let focus_id = rowInfo.original[selectionKey]

      let select_next = direction => {
        let skeys = state.pageRows.map(r => r[selectionKey])
        let next_index = (skeys.length + skeys.indexOf(focus_id) + direction) % skeys.length
        let next_id = skeys[next_index]
        changeFocus(next_id)
        document.querySelector(`.kbd_focus_${next_id}`).focus()
      }
      // call external
      let tr_props = callIfFunc(getTrProps || {}, state, rowInfo, column)
      return {
        ...tr_props,
        onClick: () => changeFocus(focus_id),
        // if arrow up select_next(-1) and arrow down select_next(1)
        onKeyDown: event => {
          if (event.key === 'ArrowUp') {
            select_next(-1)
            event.preventDefault()
          } else if (event.key === 'ArrowDown') {
            select_next(1)
            event.preventDefault()
          }
        },
        tabIndex: 0, // Make the row focusable
        style: {
          outline: 'none',
          cursor: 'pointer',
          backgroundColor: focused == focus_id ? '#CCC' : null,
        },
        className: clsx(tr_props.className, `kbd_focus_${focus_id}`),
      }
    }
    return {getTrProps: _getTrProps}
  }
}

export const SelectableTable = ({
  selectable = true,
  selectionKey, // data property that should be used for selection tracking
  selection,
  selectionDep, // reset selected if dependency changed
  unselectable, // list of selection key values, rows that should be unselectable
  onSelectionChange,
  focused,
  onFocusChange, // receives selection key on mouse click
  onCopyToClipboard,
  data,
  columns,
  menuPortal,
  className,
  compact = false, // less top/bottom padding for cells
  flex = false, // use outer space, set flex=1
  showCount = false, // show row count in pagination
  showFooter = false,
  persist = false, // persistent selected columns + sorting/filtering (will also enable query param decoding)
  persistKey,
  // render additional stuff accessing fully computed table state
  renderHooked,
  renderTable = true, // emit rendered default react table
  ...props
}) => {
  let {i18n} = usePartContext()
  let ref = useRef()

  let {cols, dropdown} = useSelectableColumns(columns, persist, persistKey)

  unselectable = useMemo(
    () =>
      isFunction(unselectable) ? data.filter(unselectable).map(r => r[selectionKey]) : unselectable,
    [unselectable]
  )

  let {columns: groupableCols, pivotBy} = useGroupableColumns(cols, persist, persistKey)
  if (groupableCols !== cols) {
    cols = groupableCols
    props = {...props, pivotBy}
  }

  props = {...props, ...useFocusManagement(focused, selectionKey, onFocusChange, props.getTrProps)}

  // selection aspect
  // selected[0] stores toggleAll with 3 states: 0, 1, 2=indeterminate
  let [selected, setSelected] = useState({})
  let resetSelection = () => {
    if (size(selected) > 0) {
      setSelected({})
      if (onSelectionChange) onSelectionChange([])
    }
  }
  useEffect(() => {
    if (selectionDep !== undefined) {
      resetSelection()
    }
  }, [selectionDep])

  if (!selectionKey) {
    log.warn('no selectionKey')
  }
  let unselectable_set = useMemo(() => (unselectable ? new Set(unselectable) : null), [
    unselectable,
  ])
  let selectable_columns = useMemo(
    () => {
      if (!selectable) return cols
      // additionally keep track of selection state in memo
      let _selected = selected

      let _numSelected = sel => filterItems(sel, (k, v) => k !== '0' && v).length
      let numSelected = _numSelected(selected)

      let update = sel => {
        setSelected(sel)
        _selected = sel
        numSelected = _numSelected(sel)
      }

      if (selection) {
        // clear selected, if selection is empty
        if (selection.length == 0) {
          if (numSelected > 0) update({})
        }
        // todo: set/unset selected items
      }

      let buildSelectedSet = rawSelected =>
        filterItems(
          rawSelected,
          (id, value) => id !== '0' && value && (!unselectable_set || !unselectable_set.has(id))
        ).map(e => e[0])

      let updateMixed = (nextSelected, nextValue) => {
        // XXX: depends on data
        let isMixed = data.some(r => get(nextSelected, r[selectionKey], false) !== nextValue)
        nextSelected[0] = isMixed ? 2 : nextValue ? 1 : 0
      }

      let toggleSelect = row => {
        let nextValue = !_selected[row[selectionKey]]
        let nextSelected = {..._selected, [row[selectionKey]]: nextValue}
        updateMixed(nextSelected, nextValue)
        update(nextSelected)

        if (onSelectionChange) onSelectionChange(buildSelectedSet(nextSelected))
      }

      let toggleSelectAll = e => {
        // apply toggle only to visible (filtered/sorted) data
        let {sortedData} = ref.current.getResolvedState()
        if (sortedData.length > 0 && sortedData[0]._aggregated) {
          // groupBy mode - iterate over all groups
          var nextSelected = dict(
            flatten(
              sortedData.map(groupRow =>
                groupRow._subRows.map(e => [e._original[selectionKey], !_selected[0]])
              )
            )
          )
        } else {
          if (unselectable_set) {
            sortedData = sortedData.filter(e => !unselectable_set.has(e._original[selectionKey]))
          }
          var nextSelected = dict(sortedData.map(e => [e._original[selectionKey], !_selected[0]]))
        }
        nextSelected[0] = _selected[0] ? 0 : 1
        update(nextSelected)

        if (onSelectionChange) onSelectionChange(buildSelectedSet(nextSelected))
      }

      let filterlabel = () => {
        if (numSelected > 0) {
          return (
            <Label
              title="Copy to Clipboard."
              className={clsx(styles.selectionCountLabel, styles.clickableCountLabel)}
              onClick={copyToClipboard}
            >
              {numSelected}
            </Label>
          )
        }
        return <Label className={styles.selectionCountLabel}>{numSelected}</Label>
      }
      let toggleSelectSome = (currentValue, ids) => {
        let nextValue = currentValue == 0
        let someSelected = dict(ids.map(id => [id, nextValue]))
        let nextSelected = {..._selected, ...someSelected}
        updateMixed(nextSelected, nextValue)
        update(nextSelected)
        if (onSelectionChange) onSelectionChange(buildSelectedSet(nextSelected))
      }

      let copyToClipboard = () => {
        let l = buildSelectedSet(_selected)
        if (onCopyToClipboard) {
          onCopyToClipboard(l)
        } else {
          navigator.clipboard.writeText(l.join(', '))
        }
      }

      return [
        {
          id: 'checkbox',
          accessor: '_selected_',
          Cell: ({value, original, aggregated, subRows, ...more}) => {
            if (aggregated) {
              // ugly fugly id generation, only for "htmlFor" support and less "chrome issues"
              let ckid = `check_${subRows[0]._original[selectionKey]}`
              return (
                <Checkbox
                  id={ckid}
                  checked={value === 1}
                  indeterminate={value === 2}
                  onChange={e => {
                    e.stopPropagation()
                    toggleSelectSome(value, subRows.map(r => r._original[selectionKey]))
                  }}
                />
              )
            } else if (unselectable_set && unselectable_set.has(original[selectionKey])) {
              return null
            } else
              return (
                <Checkbox
                  id={`check_${original[selectionKey]}`}
                  checked={value}
                  onChange={e => {
                    e.stopPropagation() // do not trigger onClick on parents (getTrProps)
                    toggleSelect(original)
                  }}
                />
              )
          },
          style: {textAlign: 'center'},
          Header: () => {
            return (
              <Checkbox
                id="checkall"
                checked={_selected[0] === 1}
                ref={input => (input ? (input.indeterminate = _selected[0] === 2) : null)}
                onChange={toggleSelectAll}
              />
            )
          },
          Filter: ({filter, onChange}) => filterlabel(),
          sortable: false,
          resizable: false,
          width: compact ? 25 : 50,
          aggregate: vals => {
            let num_selected = vals.filter(v => v).length
            if (num_selected == 0) {
              return 0
            } else if (num_selected == vals.length) {
              return 1
            }
            return 2
          },
          Footer: showFooter
            ? ({data}) => sum(data.map(e => (e._aggregated ? e._subRows.length : 1)))
            : null,
        },
        ...cols,
      ]
    },
    // hacked dependency in updateMixed by at least depending on data.length
    [cols, selectable, ref, data.length, selection, selected, unselectable_set]
  )

  let {TbodyComponent, overflowActive} = useBodyScrollAdaptation()

  // recalc data if selection changes
  data = useMemo(
    () =>
      !selectable ? data : data.map(e => ({...e, _selected_: selected[e[selectionKey]] === true})),
    [data, selectable, selected]
  )

  let hookTableState = (state, makeTable, instance) => {
    // filter selection state down on visible rows/data
    let filtered = new Set(state.pageRows.map(r => String(r[selectionKey])))
    let selectedFiltered = mapItems(
      selected,
      selectionKey => selectionKey,
      (selectionKey, value) => value && filtered.has(selectionKey)
    )
    return (
      <>
        {renderHooked
          ? renderHooked({
              selected,
              selectedFiltered,
              sortedData: state.sortedData,
              internalColumns: state.columns,
              pivotBy: state.sortedData[0]?._pivotVal ? [state.sortedData[0]._pivotID] : [],
            })
          : null}
        {renderTable && makeTable()}
      </>
    )
  }
  let _onFilteredChange = filtered => {
    resetSelection()
  }

  let persistedState = persist
    ? usePersistentTableState({
        persistKey,
        onFilteredChange: _onFilteredChange,
        data,
        pivotBy: props.pivotBy,
      })
    : {}

  let expansionProps = useStableExpansion(
    selectionKey,
    persist,
    persistKey,
    size(props.pivotBy) > 0
  )

  props = {
    ...rt_defaults(props, data, i18n, {showCount}),
    ...props,
    ...persistedState,
    ...expansionProps,
    className: clsx(
      flex && styles.flex,
      compact && styles.compact,
      overflowActive && styles.bodyOverflow,
      className
    ),
    TbodyComponent,
    defaultSortMethod,
  }

  let hookedTable = (
    <ReactTable data={data} columns={selectable_columns} {...props} ref={ref}>
      {hookTableState}
    </ReactTable>
  )

  if (menuPortal) {
    return (
      <>
        <AutoPortal target={menuPortal}>{dropdown}</AutoPortal>
        {hookedTable}
      </>
    )
  }

  return hookedTable
}

const useBodyScrollAdaptation = () => {
  /* if the tbody get's a scrollbar, adapt thead/tfoot spacing (on the right side)
   */
  let [overflowActive, bodyRef] = useOverflowDetection()
  //~ overflowActive = true
  // build rt-tbody
  let TbodyComponent = useCallback(
    ({children, className, ...rest}) => (
      <div
        className={clsx('rt-tbody', className)}
        {...rest}
        ref={bodyRef}
        // style={{overflowX: 'hidden'}}
      >
        {children}
      </div>
    ),
    [bodyRef]
  )

  return {TbodyComponent, overflowActive}
}

// see https://www.npmjs.com/package/react-table-6#props
const rt_defaults = (props, data, i18n, options) => {
  let defaults = useMemo(
    () => ({
      // general behaviour
      collapseOnDataChange: false,

      // Text
      previousText: <Icon name="long arrow alternate left" size="large" />,
      nextText: <Icon name="long arrow alternate right" size="large" />,
      loadingText: 'Lade...',
      noDataText: '',
      pageText: sortedData => (
        <>
          {options.showCount && (
            <span style={{paddingRight: '4em'}}>
              {i18n.count}: {size(data)}{' '}
              {size(sortedData) !== size(data) ? `(${size(sortedData)})` : null}
            </span>
          )}
          {i18n.tablePage}
        </>
      ),
      ofText: i18n.of,
      rowsText: i18n.tableRows,
      showPagination: false,

      NoDataComponent: () => null,
      // Accessibility Labels
      pageJumpText: 'jump to page',
      rowsSelectorText: 'rows per page',
      subRowsLimit: 200,
      // NOTE: if you set minRows to 0 then you get rid of empty padding rows BUT your table formatting will also look strange when there are ZERO rows in the table
      minRows: 0, // controls the minimum number of rows to display - default will be `pageSize`
    }),
    [options.showCount, size(data)]
  )

  // rt_defaults set showPagination to false, so show *all* rows if
  // defaultPageSize or pageSize are not externally managed and showPagination is not set
  // so if you control showPagination
  // you will probably also want to control pageSize
  if (!props.defaultPageSize && !props.pageSize && !props.showPagination) {
    defaults.pageSize = size(data)
  }
  return defaults
}

const ColumnHeaderButton = ({icon, ...props}) => (
  <Button style={{padding: '0.25em', zIndex: 100}} size="tiny" secondary floated="right" {...props}>
    <VIcon style={{margin: '0'}} name={icon} />
  </Button>
)

const useSelectableColumns = (columns, persist, persistKey) => {
  /* manage column selection (optional columns)
  read `selectable` and `autoselect` from column config
  also handle .visible column property
  XXX: does not handle column-header-groups
   */
  let i18n = useSelector(Sel.i18n)
  let get_colid = col => col.id || col.accessor

  let unlabeledSelection = true // no multi-select labels
  let persist_auto_selected = columns
    .filter(c => c.selectable && c.autoselect)
    .map(c => get_colid(c))

  // reset persisted state if count changes
  // XXX: this can lead to surprising version changes, probably not a good idea
  let version = persist_auto_selected.length

  // only dummy if columns are empty
  let [selected, setSelected] = persist
    ? usePersistent(buildPersistKey('TableColSelect', persistKey), persist_auto_selected, version)
    : useState(persist_auto_selected)

  let byId = dict(columns.map(c => [get_colid(c), c]))

  let col_ids = columns.filter(c => c.selectable).map(col => get_colid(col))
  let selectable = col_ids

  let options = selectable.map(colid => {
    return {
      text: byId[colid].Header,
      key: colid,
      value: colid,
    }
  })
  let dropdown = (
    <XMenu.Dropdown
      title={i18n.get('table_choose_columns_title')}
      placeholder={i18n.get('table_choose_columns')}
      multiple
      search
      scrolling
      selection
      clearable
      compact
      direction="left"
      renderLabel={unlabeledSelection ? () => null : undefined}
      value={selected}
      options={options}
      onChange={(e, data) => setSelected(data.value)}
      text={selected.length > 0 ? `${selected.length} Spalten` : 'Wähle Spalten..'}
      style={{minWidth: 200}}
    />
  )
  let cols = useMemo(() => {
    let always_selected = columns.filter(c => !c.selectable)
    let optional_selected = columns.filter(c => c.selectable && selected.includes(get_colid(c)))

    return [...always_selected, ...optional_selected].map(col => {
      if (!col.selectable) {
        return col
      }
      let colhead = col.Header
      let col_id = get_colid(col)
      let content = col_id

      if (isString(colhead) || isElement(colhead) || isFragment(colhead)) {
        content = colhead
      } else if (isFunction(colhead)) {
        content = colhead()
      }
      let onClick = event => {
        setSelected(selected.filter(sel_colid => sel_colid !== col_id))
        event.stopPropagation()
      }

      colhead = (
        <>
          <ColumnHeaderButton color="grey" icon="close" onClick={onClick} />
          <span>{content}</span>
        </>
      )
      return {...col, Header: colhead}
    })
  }, [columns, selected])

  // filter visible property (if explicity set to false)
  cols = useMemo(() => cols.filter(c => isNil(c.visible) || !!c.visible), [cols])

  return {cols, dropdown}
}

const useGroupableColumns = (columns, persist, persistKey) => {
  /* manage pivotBy on configured rows
  read `groupable` and `groupableAccessor` column config
  groupableAccessor can build a default-value independant grouping value (day resolutions on timestamps)
  */
  let [pivotBy, setPivotBy] = usePersistent(buildPersistKey('TablePivotBy', persistKey), null)

  let cols = useMemo(() => {
    // disabled pivotBy if not in available columns
    if (size(pivotBy) > 0) {
      if (!columns.map(c => c.id || c.accessor).includes(pivotBy[0])) {
        pivotBy = null
      }
    }

    let some_groupable = false
    let modified_cols = columns.map(c => {
      let handle_col = c => {
        if (!c.groupable) return c
        some_groupable = true
        let col_id = c.id || c.accessor
        let is_active = pivotBy?.includes(col_id)
        if (is_active && c.groupableAccessor) {
          c = {...c, accessor: r => c.groupableAccessor(r)}
        }
        return {
          ...c,
          Header: (
            <span>
              <ColumnHeaderButton
                active={is_active}
                icon="pivot table"
                title="Gruppieren"
                onClick={e => {
                  e.stopPropagation()
                  setPivotBy(is_active ? null : [col_id])
                }}
              />
              {c.Header}
            </span>
          ),
        }
      }
      // isArray if header-grouped columns
      return isArray(c) ? c.map(handle_col) : handle_col(c)
    })
    if (some_groupable) {
      return modified_cols
    } else {
      // return unmodified
      return columns
    }
  }, [columns, pivotBy])
  return {columns: cols, pivotBy: pivotBy || []}
}

const useStableExpansion = (expandedKey, persist, persistKey, isPivotBy) => {
  /* use getExpandedState callback to manage expansion data driven
  instead of index/nestingPath driven
  getExpandedState is patched into react-table-6
  */
  let state = null
  if (persist) {
    state = usePersistent(buildPersistKey('TableExpansion', persistKey), {})
  } else {
    state = useState({})
  }
  let [expanded, setExpanded] = state

  let onExpandedChange = useCallback(
    (newExpanded, nestingPath, e, cellInfo) => {
      if (!cellInfo.aggregated) {
        let id = cellInfo.original[expandedKey]
        let val = get(newExpanded, cellInfo.nestingPath)
        setExpanded({...expanded, [id]: val})
      }
    },
    [expanded, expandedKey]
  )

  let getExpandedState = useCallback(
    rowInfo => {
      return expanded[(rowInfo.original?.[expandedKey])]
    },
    [expanded, expandedKey]
  )

  if (isPivotBy) {
    // simple version
    return {expanded, onExpandedChange: setExpanded}
  }

  if (!expandedKey) return {}
  return {onExpandedChange, getExpandedState}
}

export const TranslatedTable = ({
  columns = [],
  persist = false,
  persistKey,
  compact = false, // less top/bottom padding for cells
  flex = false, // use outer space, set flex=1
  className,
  menuPortal,
  showCount = false, // show row count in pagination
  hidePaginationOnGroupBy = false,
  expandedKey,
  ...props
}) => {
  let {i18n} = usePartContext()
  let persistedState = persist
    ? usePersistentTableState({persistKey, data: props.data, pivotBy: props.pivotBy})
    : {}
  let {TbodyComponent, overflowActive} = useBodyScrollAdaptation()

  let expansionProps = useStableExpansion(expandedKey, persist, persistKey, size(props.pivotBy) > 0)

  props = {
    ...rt_defaults(props, props.data, i18n, {showCount}),
    ...props,
    ...persistedState,
    ...expansionProps,
    className: clsx(
      flex && styles.flex,
      compact && styles.compact,
      overflowActive && styles.bodyOverflow,
      className
    ),
    TbodyComponent,
    defaultSortMethod,
  }

  let {cols, dropdown} = useSelectableColumns(columns, persist, persistKey)

  let {columns: groupableCols, pivotBy} = useGroupableColumns(cols, persist, persistKey)
  if (groupableCols !== cols) {
    cols = groupableCols
    props = {...props, pivotBy}
    if (hidePaginationOnGroupBy && size(pivotBy)) {
      props.showPagination = false
    }
  }

  return (
    <>
      {menuPortal ? <AutoPortal target={menuPortal}>{dropdown}</AutoPortal> : null}
      <ReactTable columns={cols} {...props} />
    </>
  )
}
